/**
 * Firestore write helpers for the wedding-day timeline (Plan dnia).
 * Mirrors the guests / tasks modules — explicit weddingId, no implicit
 * context, easy to unit-test in isolation.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { DayPlanEvent } from "@amazing-wedding/shared-types";

export type DayPlanEventDraft = Omit<DayPlanEvent, "id" | "createdAt" | "updatedAt">;
export type DayPlanEventPatch = Partial<Omit<DayPlanEvent, "id" | "createdAt" | "updatedAt">>;

const COL = (weddingId: string) => collection(db, "weddings", weddingId, "dayPlanEvents");

/** Firestore writeBatch op limit — chunk clears/seeds for unusually long timelines. */
const BATCH_OP_LIMIT = 500;

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

export async function createDayPlanEvent(weddingId: string, draft: DayPlanEventDraft) {
  const ref = await addDoc(COL(weddingId), {
    ...draft,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateDayPlanEvent(
  weddingId: string,
  eventId: string,
  patch: DayPlanEventPatch,
) {
  await updateDoc(doc(db, "weddings", weddingId, "dayPlanEvents", eventId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteDayPlanEvent(weddingId: string, eventId: string) {
  await deleteDoc(doc(db, "weddings", weddingId, "dayPlanEvents", eventId));
}

/**
 * Re-create a just-deleted event at its original id (powers the mobile "Cofnij"
 * undo). Reusing the id keeps it idempotent against a double-tapped undo and
 * preserves any `groupId` shared-edit links. Timestamps are written fresh
 * rather than restored: `createdAt` reaches the client as a Firestore Timestamp
 * (the schema's ISO type is a Zod-only fiction), so round-tripping it is more
 * fragile than minting a new one, and sort order keys off `order`, not time.
 * Optional fields are only set when present so Firestore never sees `undefined`.
 */
export async function restoreDayPlanEvent(weddingId: string, event: DayPlanEvent) {
  const data: Record<string, unknown> = {
    icon: event.icon,
    personId: event.personId,
    hour: event.hour,
    minute: event.minute,
    title: event.title,
    notes: event.notes,
    durationMinutes: event.durationMinutes,
    order: event.order,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (event.groupId !== undefined) data.groupId = event.groupId;
  if (event.allColumns !== undefined) data.allColumns = event.allColumns;
  await setDoc(doc(db, "weddings", weddingId, "dayPlanEvents", event.id), data);
}

/** Seed a fresh timeline from a list of drafts; used by "Wczytaj szablon". */
export async function seedDayPlan(weddingId: string, drafts: DayPlanEventDraft[]) {
  if (drafts.length === 0) return;
  for (const part of chunk(drafts, BATCH_OP_LIMIT)) {
    const batch = writeBatch(db);
    for (const draft of part) {
      const ref = doc(COL(weddingId));
      batch.set(ref, {
        ...draft,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }
}

/** Wipe the whole timeline (used by the reset action). */
export async function clearDayPlan(weddingId: string, eventIds: string[]) {
  if (eventIds.length === 0) return;
  for (const ids of chunk(eventIds, BATCH_OP_LIMIT)) {
    const batch = writeBatch(db);
    for (const id of ids) {
      batch.delete(doc(db, "weddings", weddingId, "dayPlanEvents", id));
    }
    await batch.commit();
  }
}

/**
 * Remove every timeline event. Reads doc ids from Firestore so nothing
 * survives a stale React snapshot or a partner's concurrent edit.
 */
export async function resetDayPlan(weddingId: string) {
  const snap = await getDocs(COL(weddingId));
  await clearDayPlan(
    weddingId,
    snap.docs.map((d) => d.id),
  );
}

/** Batch-update a set of events with individual patches in a single write round-trip. */
export async function batchPatchDayPlanEvents(
  weddingId: string,
  updates: Array<{ eventId: string; patch: DayPlanEventPatch }>,
) {
  if (updates.length === 0) return;
  for (const part of chunk(updates, BATCH_OP_LIMIT)) {
    const batch = writeBatch(db);
    for (const { eventId, patch } of part) {
      batch.update(doc(db, "weddings", weddingId, "dayPlanEvents", eventId), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }
}
